/** @format */

import React, { useState, useEffect, useRef, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";

import { getSession } from "./store/actions";
import { REDIRECT_AFTER_LOGIN_KEY, PUBLIC_PATHS } from "./constants/redirectAfterLogin";

//import IndexScreen from "./screens/index";
import LandingScreen from "./screens/landing";

//import { delay } from "lodash";
import AuthenticatedApp from "./screens/AuthenticatedApp";

import "./assets/styles/main.scss";
import "./assets/styles/App.scss";

import { Button } from "semantic-ui-react";
import Footer from "./components/Footer/index.js";
import { BackendStatusBanner } from "./components/BackendStatusBanner";
import { AxiosContext, AxiosProvider } from "./context/AxiosContext.js";
import { SwapProvider } from "./context/SwapContext";
import { NotificationProvider } from "./context/NotificationContext";
import { UserProvider } from "./context/UserContext";
import { StationProvider } from "./context/StationContext";

global.adminLikeGroups = [
  "overall_admin",
  "Connect44",
  "Connect44-service",
  "Connect44-swapper",
  "EMI Indonesia",
];

let getSessionRanOnce = false;

/**
 * Save current URL when not logged in so we can redirect after login.
 * Only saves protected paths (not login, reset-password, etc.).
 */
function saveRedirectIfProtected() {
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  const full = path + window.location.search + window.location.hash;
  const isPublic = PUBLIC_PATHS.some((p) => path === p || path.startsWith(p + "/"));
  if (!isPublic && full.startsWith("/")) {
    try {
      sessionStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, full);
    } catch (_) {}
  }
}

/**
 * @component App - main entry
 *  - returns AuthenticatedApp or LandingScreen from session.loggedIn (single source of truth)
 */
function App(props) {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);

  const [showTopBtn, setShowTopBtn] = useState();
  const [showFooter, setShowFooter] = useState();

  useEffect(() => {
    if (getSessionRanOnce) return;
    getSessionRanOnce = true;
    dispatch(getSession());
  }, [dispatch]);

  if (!session.loggedIn) saveRedirectIfProtected();

  useEffect(() => {
    window.addEventListener("scroll", () => {
      setShowTopBtn(window.scrollY > 400);
      setShowFooter(window.scrollY > window.innerHeight);
    });
    return () => window.removeEventListener("scroll");
  }, []);

  return (
    <AxiosProvider>
      <SwapProvider>
        <UserProvider>
          <NotificationProvider>
            <StationProvider>
              <SessionSync />
              <BackendStatusBanner />
              <div className="App" data-testid="app">
                {!session.loggedIn ? (
                  <LandingScreen />
                ) : (
                  <AuthenticatedApp />
                )}
                {session.loggedIn && <Footer />}

                {showTopBtn && (
                  <Button
                    style={{
                      position: "fixed",
                      bottom: 40,
                      right: 20,
                    }}
                    basic
                    icon="arrow up"
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: "smooth" })
                    }
                  />
                )}
              </div>
            </StationProvider>
          </NotificationProvider>
        </UserProvider>
      </SwapProvider>
    </AxiosProvider>
  );
}
export default App;

/**
 * Syncs Redux session to AxiosContext. Only updates when token actually changes
 * to avoid redundant re-renders and duplicate request triggers downstream.
 */
const SessionSync = () => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);
  const { setAccessToken } = useContext(AxiosContext);
  const prevLoggedIn = useRef(undefined);
  const lastTokenRef = useRef(undefined);

  useEffect(() => {
    if (session.loggedIn && session.token) {
      if (lastTokenRef.current !== session.token) {
        lastTokenRef.current = session.token;
        setAccessToken(session.token);
      }
    } else {
      if (prevLoggedIn.current === true) {
        lastTokenRef.current = undefined;
        setAccessToken(null);
      }
      prevLoggedIn.current = session.loggedIn;
    }
    if (session.loggedIn) prevLoggedIn.current = true;
  }, [session.loggedIn, session.token, setAccessToken]);

  useEffect(() => {
    let storageTimeout;
    const handleStorage = () => {
      clearTimeout(storageTimeout);
      storageTimeout = setTimeout(() => dispatch(getSession()), 50);
    };
    window.addEventListener("storage", handleStorage);
    return () => {
      clearTimeout(storageTimeout);
      window.removeEventListener("storage", handleStorage);
    };
  }, [dispatch]);

  return null;
};
